import { useTranslation } from 'next-i18next'
import { useRef } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import SelectionButton from '~/bootcamp/Activities/components/SelectionButton'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFractionDivideZero from './useFractionDivideZero'

function FractionDivideZero({
  answerState,
  actionButtonsFC,
  autofocus = true,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const { numerator, options, formValid, selectedAnswer, onInputChange, handleCheckAnswer, goToNextQuestion } =
    useFractionDivideZero({
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
    })

  // 'undefined' is an option's own text here, not a missing value.
  useEmitInstance(
    onInstanceChange,
    numerator != null && options?.length ? `\\frac{${numerator}}{0}; options: ${options.join(', ')}` : null,
  )
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { t } = useTranslation('bootcamp')

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <span className="text-2xl">{numerator}</span>
        <Divider />
        <span className="text-2xl">0</span>
      </FractionsActivityWrapper>
      <div className="grid grid-cols-2 gap-2 w-full sm:w-4/5 md:w-4/6 lg:w-4/5 xl:w-3/4">
        {options &&
          options.map((option) => (
            <SelectionButton
              className="p-2 flex-1"
              isSelected={selectedAnswer === option}
              isWrong={selectedAnswer === option && answerState === false}
              key={`${option}`}
              onClick={onInputChange.bind(null, option)}
            >
              <span>{option === 'undefined' ? t('fractions.undefined') : option}</span>
            </SelectionButton>
          ))}
      </div>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: goToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionDivideZero
